<template>
    <div>
        <v-container>

            <!-- TITLE -->
            <v-row v-if="!hideTitle">
                <v-col cols="12">
                    <div class="title text-center">
                        {{ defaultExercise ? $t('common.edit') : $t('common.create') }}
                        {{ `${$t('common.new')} ${$tc('exercises.exercises', 1)}` }}
                    </div>
                </v-col>
            </v-row>

            <!-- BOTTOM BUTTONS -->
            <v-row v-if="!hideButtons && !bottomBtn" class="bottom-container">
                <v-col cols="12" class="text-right">
                    <v-btn :disabled="!isValid" :loading="saving" @click="next" color="primary"
                        class="black--text normal-btn" depressed>
                        {{ carousel === 1 ? $t('common.save') : $t('common.continue') }}
                    </v-btn>
                </v-col>
            </v-row>

            <!-- EXERCISE -->
            <v-row>
                <v-col cols="12">
                    <v-carousel :show-arrows="false" light hide-delimiters touchless v-model="carousel" height="auto">
                        <!-- NAME & CATEGORIES -->
                        <v-carousel-item>
                            <v-row>
                                <v-col cols="12">
                                    <kimsa-text-field :label="$t('exercises.exercise_name')"
                                        :placeholder="$t('exercises.exercise_name_example')" :value="name"
                                        @change="name = $event" outlined dense no-elevation bolder-label force-value />
                                </v-col>
                                <v-col cols="12">
                                    <kimsa-tags-picker ref="tagsPicker" @change="tags = $event" :default-tags="tags"
                                        :label="$t('exercises.tags')" bolder-label force-value />
                                </v-col>
                                <v-col cols="12">
                                    <exercise-types-picker ref="typesPicker" :default-categories="categories"
                                        :default-types="types" @change:categories="categories = $event"
                                        @change:types="types = $event" force-value />
                                </v-col>
                            </v-row>
                        </v-carousel-item>

                        <!-- IMAGE & EQUIPMENTS -->
                        <v-carousel-item>
                            <v-row>
                                <v-col cols="12">
                                    <kimsa-textarea-field outlined no-elevation bolder-label
                                        :label="$t('exercises.description')" @change="description = $event"
                                        :value="description" force-value />
                                </v-col>
                                <v-col cols="6">
                                    <kimsa-text-field :label="$t('common.duration')"
                                        :placeholder="$t('exercises.exercise_name_example')" :value="duration"
                                        @change="duration = $event" outlined dense no-elevation bolder-label
                                        force-value />
                                </v-col>
                                <v-col cols="6">
                                    <kimsa-combobox :label="$t('exercises.unit')" outlined dense bolder-label
                                        :value="unit" @change="unit = $event" :items="unitsOptions" />
                                </v-col>
                                <v-col cols="12">
                                    <div class="label mb-1">
                                        {{ $t('exercises.add_video_and_image') }}
                                    </div>
                                    <file-uploader class="mr-2" ref="imgUploader" mime-types="image/*"
                                        @success="onImgUploaded" relative-path="images/routines" :limit-size="2"
                                        @change="onChangeImg">
                                        <template slot="button">
                                            <div class="img-container">
                                                <div v-if="imgName" class="img-preview"
                                                    :style="`background-image: url(${imgUrl})`">
                                                    <div class="img-name" @click.stop="clearImg">
                                                        {{ imgName }}
                                                        <v-icon x-small color="error">$close</v-icon>
                                                    </div>
                                                </div>
                                                <div class="img-content" v-else>
                                                    <v-icon size="28">$upload_image</v-icon>
                                                    <div class="load-image-title">
                                                        {{ $t('common.load_image') }}
                                                    </div>
                                                    <div class="load-image-subtitle">
                                                        {{ $t('common.load_image_max_size') }} <span
                                                            class="load-image-subtitle bolder">2MB</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                    </file-uploader>
                                    <file-uploader ref="videoUploader" @success="onVideoUploaded"
                                        relative-path="images/routines" :limit-size="20" @change="onChangeVideo">
                                        <template slot="button">
                                            <div class="img-container">
                                                <div v-if="videoName" class="img-preview"
                                                    :style="`background-image: url(${videoThumb})`">
                                                    <div class="img-name" @click.stop="clearVideo">
                                                        {{ videoName }}
                                                        <v-icon x-small color="error">$close</v-icon>
                                                    </div>
                                                </div>
                                                <div class="img-content" v-else>
                                                    <v-icon size="28">mdi-cloud-upload-outline</v-icon>
                                                    <div class="load-image-title">
                                                        {{ $t('common.load_video') }}
                                                    </div>
                                                    <div class="load-image-subtitle">
                                                        {{ $t('common.load_video_max_size') }} <span
                                                            class="load-image-subtitle bolder">20MB</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                    </file-uploader>
                                </v-col>
                                <v-col cols="12">
                                    <div class="label mb-1">
                                        {{ $t('exercises.exercise_requires_specific_equipment') }}
                                    </div>
                                    <choose-equipments hide-title :selected="equipments"
                                        @change="equipments = $event" />
                                </v-col>
                            </v-row>
                        </v-carousel-item>
                    </v-carousel>
                    <v-sheet v-if="!hideButtons" height="60" color="transparent" />
                </v-col>
            </v-row>
        </v-container>
        <v-row v-if="!hideButtons && bottomBtn">
            <v-col cols="12" class="text-right">
                <v-btn :disabled="!isValid" :loading="saving" @click="next" color="primary"
                    class="black--text normal-btn" depressed>
                    {{ carousel === 1 ? $t('common.save') : $t('common.continue') }}
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import ExerciseTypesPicker from "@/components/coach/routines/ExerciseTypesPicker";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaTagsPicker from "@/components/common/inputs/KimsaTagsPicker";

import { db } from '@/utils/Firebase'
import FileUploader from "@/components/common/FileUploader";
import ChooseEquipments from "@/components/student/ChooseEquipments";
import KimsaTextareaField from "@/components/common/inputs/KimsaTextareaField";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";

export default {
    name: "ExerciseCreator",
    components: {
        KimsaCombobox,
        KimsaTextareaField,
        ChooseEquipments, FileUploader, KimsaTagsPicker, KimsaTextField, ExerciseTypesPicker
    },
    props: {
        defaultExercise: {
            type: Object,
            default: () => {
            }
        },
        newExercise: {
            type: Object,
            default: () => {
            }
        },
        hideButtons: {
            type: Boolean,
            default: false
        },
        hideTitle: {
            type: Boolean,
            default: false
        },
        bottomBtn: {
            type: Boolean,
            default: false
        },
    },
    data(vm) {
        return {
            saving: false,
            exerciseId: null,
            setExerciseStep: 1,
            carousel: 0,
            name: '',
            description: '',
            duration: 10,
            unit: vm.$tc('common.seconds', 2),
            videoName: '',
            videoThumb: '',
            imgName: '',
            imgUrl: '',
            equipments: [],
            categories: [],
            types: [],
            tags: [],
            uploadedImg: null,
            uploadedVideo: null
        }
    },
    async mounted() {
        const vm = this
        vm.setDefaults()
    },
    computed: {
        unitsOptions() {
            const vm = this
            return [
                vm.$tc('common.seconds', 2),
                vm.$tc('common.minutes', 2),
                vm.$tc('common.hours', 2),
            ]
        },
        isValid() {
            const vm = this
            return !(vm.carousel === 0 && (!vm.name || vm.categories.length === 0 || vm.types.length === 0));
        },
        isCompany(vm = this) {
            return vm.account?.accountType === 'company'
        },
        exercisePayload() {
            const vm = this
            let companyId = vm.defaultExercise?.companyId || vm.account?.coachSubscription?.companyId || null
            if (vm.isCompany) companyId = vm.currentUser.uid

            return {
                name: vm.name || '',
                owner: vm.account && vm.account.appTeam ? 'app' : 'user',
                coachId: vm.defaultExercise?.coachId || vm.currentUser.uid,
                companyId: companyId,
                description: vm.description || '',
                duration: vm.duration || '',
                unit: vm.unit || '',
                equipments: vm.equipments || [],
                categories: vm.categories || [],
                types: vm.types || [],
                tags: vm.tags || [],
                img: vm.uploadedImg?.url || vm.defaultExercise?.img || '',
                imgSavePath: vm.uploadedImg?.savePath || vm.defaultExercise?.imgSavePath || '',
                video: vm.uploadedVideo || vm.defaultExercise?.video || null
            }
        }
    },
    methods: {
        next() {
            const vm = this
            vm.carousel < vm.setExerciseStep ? vm.continueStepper() : vm.save()
        },
        async onImgUploaded({ downloadURL, savePath }) {
            const vm = this
            vm.uploadedImg = { url: downloadURL, savePath: savePath }
        },
        async onVideoUploaded({ downloadURL, savePath }) {
            const vm = this
            vm.uploadedVideo = { tempUrl: downloadURL, savePath: savePath }
        },
        goBack() {
            const vm = this
            if (vm.carousel === 0) return vm.$emit('cancel')
            vm.carousel--
        },
        continueStepper() {
            const vm = this
            vm.carousel++
        },
        async save() {
            const vm = this
            vm.saving = true

            if (vm.imgUrl.includes('blob:')) await vm.$refs.imgUploader.upload()
            if (vm.videoThumb.includes('blob:')) await vm.$refs.videoUploader.upload()

            let response = null
            if (vm.defaultExercise) {
                response = await db.collection('exercises')
                    .doc(vm.defaultExercise.objectID)
                    .update(vm.exercisePayload)
            } else {
                response = await db.collection('exercises')
                    .add(vm.exercisePayload)

                vm.exerciseId = response.id
            }

            vm.saving = false
            const id = vm.defaultExercise && vm.defaultExercise.objectID || vm.exerciseId
            vm.$emit('success', {
                ...vm.exercisePayload,
                objectID: id,
            })
        },
        setDefaults() {
            const vm = this

            if (vm.newExercise) {
                vm.name = vm.newExercise.name || ''
                vm.categories = vm.newExercise.categories || []
                vm.types = vm.newExercise.types || []
                return
            }

            if (!vm.defaultExercise) return

            vm.name = vm.defaultExercise.name || ''
            vm.description = vm.defaultExercise.description || ''
            vm.duration = vm.defaultExercise.duration || 10
            vm.unit = vm.defaultExercise.unit || vm.$tc('common.seconds', 2)
            vm.categories = vm.defaultExercise.categories || []
            vm.types = vm.defaultExercise.types || []
            vm.tags = vm.defaultExercise.tags || []
            vm.equipments = vm.defaultExercise.equipments || []
            vm.imgName = vm.getFileName(vm.defaultExercise?.imgSavePath)
            vm.imgUrl = vm.defaultExercise.img || ''
            vm.videoName = vm.getFileName(vm.defaultExercise.video?.savePath)
            vm.videoThumb = vm.defaultExercise.video && vm.defaultExercise.video.thumb || ''
        },
        getFileName(savePath) {
            if (!savePath) return ''
            let arraySplit = savePath.split('/')
            return arraySplit[arraySplit.length - 1].split('-')[1]
        },
        reset() {
            const vm = this
            vm.carousel = 0
            vm.name = ''
            vm.description = ''
            vm.duration = 10
            vm.unit = vm.$tc('common.seconds', 2)
            vm.categories = []
            vm.equipments = []
            vm.types = []
            vm.tags = []
            vm.imgName = ''
            vm.imgUrl = ''
            vm.videoName = ''
            vm.videoThumb = ''
            vm.uploadedImg = null
            vm.uploadedVideo = null
            if (vm.$refs.typesPicker) vm.$refs.typesPicker.reset()
            if (vm.$refs.imgUploader) vm.$refs.imgUploader.reset()
            if (vm.$refs.videoUploader) vm.$refs.videoUploader.reset()
        },
        onChangeImg({ name, url }) {
            const vm = this
            vm.imgName = name
            vm.imgUrl = url
        },
        clearImg() {
            const vm = this
            vm.imgName = ''
            vm.imgUrl = ''
            vm.uploadedImg = null
            vm.$refs.imgUploader.reset()
        },
        onChangeVideo({ name, url }) {
            const vm = this
            vm.videoName = name
            vm.videoThumb = url
        },
        clearVideo() {
            const vm = this
            vm.videoName = ''
            vm.videoThumb = ''
            vm.uploadedVideo = null
            vm.$refs.videoUploader.reset()
        },
    },
    watch: {
        carousel(val) {
            const vm = this
            vm.$vuetify.goTo(0)
            vm.$emit('change:step', val)
        },
        newExercise() {
            this.setDefaults()
        },
        defaultExercise() {
            this.setDefaults()
        },
        isValid(val) {
            this.$emit('change:validation', val)
        },
        saving(val) {
            this.$emit('loading', val)
        }
    },
}
</script>

<style scoped>
.bottom-container {
    margin-top: 40px;
    background: #FFFFFF;
    border-top: 1px solid #333333;
    position: fixed;
    right: 0;
    left: 0;
    bottom: 0;
    padding: 8px 16px 16px 16px;
    z-index: 2;
}

.label {
    font-size: 15px;
    color: #333333;
    font-weight: 700;
}

.img-container {
    background: #FEFEFE;
    border: 1px solid #999999;
    border-radius: 4px;
    width: 150px;
    height: 100px;
    position: relative;
    cursor: pointer;
}

.img-content {
    position: absolute;
    text-align: center;
    left: 0;
    right: 0;
    top: 16%;
}

.load-image-title {
    font-weight: 500;
    font-size: 12px;
    line-height: 20px;
    color: #797979;
}

.load-image-subtitle {
    font-weight: 275;
    font-size: 10px;
    line-height: 14px;
    color: rgba(121, 121, 121, 0.7);
}

.img-preview {
    height: 100%;
    background-color: rgba(115, 115, 115, .05);
    background-position: center;
    background-size: contain;
}

.img-name {
    font-weight: 275;
    font-size: 10px;
    line-height: 14px;
    color: #e0e0e0;
    position: absolute;
    top: 6px;
    right: 6px;
    background-color: rgba(115, 115, 115, .75);
    padding: 2px 4px;
    border-radius: 4px;
    max-width: 91%;
    text-align: right;
    cursor: pointer;
}
</style>